import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, VideoOff, Mic, MicOff, Monitor, MonitorOff,
  PhoneOff, Settings, Maximize2, RefreshCw, AlertCircle,
  Users, CheckCircle, XCircle, Loader, BookOpen, X
} from 'lucide-react';
import { useRooms } from '../hooks/useRoom';
import { RemoteVideo } from './RemoteVideo';
import { VideoCallDebugPanel } from './VideoCallDebugPanel';
import API_URL from '../config/API_URL';
import { BookSharingModal } from './BookSharingModal';
import debounce from 'lodash.debounce';

export const VideoCallRoom = ({ roomId, currentUser, onLeave, participants }) => {
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [showReconnectButton, setShowReconnectButton] = useState(false);

  const [showBookModal, setShowBookModal] = useState(false);
  const [sharedBook, setSharedBook] = useState(null);
  const [isBookSharer, setIsBookSharer] = useState(false);
  
  const containerRef = useRef(null);
  const hasJoinedRef = useRef(false);
  const hasCalledRef = useRef(false);
  const reconnectTimerRef = useRef(null);

  const {
    socket,
    isInRoom,
    roomUsers,
    remoteStreams,
    connectionStatus,
    localVideoRef,
    localStreamRef,
    peerConnectionsRef,
    joinRoom,
    startScreenShare: hookStartScreenShare,
    callAllUsers,
    forceReconnectToPeer,
    isUsingScreenShare
  } = useRooms(roomId, `${currentUser?.first_name} ${currentUser?.last_name}`);

  const [cameraError, setCameraError] = useState(false);

  const handleBookSelect = (book) => {
  const bookData = {
    id: book.id,
    title: book.title,
    author: book.author,
    pdf_url: book.pdf_url,
    sharerId: currentUser.id,
    currentPage: 1 // Track current page
  };
  
  setSharedBook(bookData);
  setIsBookSharer(true);
  
  if (socket) {
    socket.emit('share-book', {
      roomId: roomId,
      book: bookData,
      sharer: {
        id: currentUser.id,
        name: `${currentUser.first_name} ${currentUser.last_name}`
      }
    });
  }
};

// Add a ref for the iframe:
const bookIframeRef = useRef(null);

const syncBookPosition = () => {
  if (!bookIframeRef.current || !isBookSharer) return;
  
  try {
    const iframe = bookIframeRef.current;
    const iframeWindow = iframe.contentWindow;
    
    // Try to get scroll position
    const scrollTop = iframeWindow.scrollY || 0;
    const scrollLeft = iframeWindow.scrollX || 0;
    
    // Get current page from URL
    const match = iframe.src.match(/#page=(\d+)/);
    const currentPage = match ? parseInt(match[1]) : 1;
    
    if (socket) {
      socket.emit('book-position-sync', {
        roomId: roomId,
        page: currentPage,
        scrollTop: scrollTop,
        scrollLeft: scrollLeft
      });
    }
  } catch (e) {
    // Handle cross-origin restrictions
    console.log('Cannot access iframe content due to cross-origin restrictions');
  }
};

// Add these socket listeners in the useEffect:
useEffect(() => {
  if (!socket) return;

  socket.on('book-shared', (data) => {
    if (data.roomId === roomId) {
      setSharedBook(data.book);
      setIsBookSharer(false);
    }
  });

  socket.on('book-unshared', (data) => {
    if (data.roomId === roomId) {
      setSharedBook(null);
      setIsBookSharer(false);
    }
  });

  // NEW: Listen for page sync updates
  socket.on('book-page-sync', (data) => {
    if (data.roomId === roomId && !isBookSharer && bookIframeRef.current) {
      // Update the iframe to the new page
      const currentSrc = bookIframeRef.current.src;
      const baseUrl = currentSrc.split('#')[0];
      bookIframeRef.current.src = `${baseUrl}#page=${data.page}`;
    }
  });

  return () => {
    socket.off('book-shared');
    socket.off('book-unshared');
    socket.off('book-page-sync');
  };
}, [socket, roomId, isBookSharer]);

// Handle iframe load and scroll events:
const handleBookIframeLoad = () => {
  if (!bookIframeRef.current || !isBookSharer) return;

  try {
    // Set up scroll listener
    const iframe = bookIframeRef.current;
    
    // Try to add scroll listener to iframe document
    iframe.contentWindow?.addEventListener('scroll', debouncedSync);
    
    // Add message listener for PDF.js navigation events
    const handleMessage = (event) => {
      if (event.source !== bookIframeRef.current.contentWindow) return;
      
      if (event.data && event.data.type === 'pagechange') {
        debouncedSync(); // Call sync on page change
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Poll for changes as backup
    const pollInterval = setInterval(() => {
      if (!bookIframeRef.current || !isBookSharer) {
        clearInterval(pollInterval);
        return;
      }
      
      debouncedSync(); // Use debounced sync
    }, 1000);

    return () => {
      window.removeEventListener('message', handleMessage);
      iframe.contentWindow?.removeEventListener('scroll', debouncedSync);
      clearInterval(pollInterval);
    };
  } catch (error) {
    console.error('Error setting up PDF sync:', error);
  }
};
const debouncedSync = useRef(
  debounce(syncBookPosition, 100) // Sync every 100ms max
).current;

  // Camera error detection
  useEffect(() => {
    if (connectionStatus === 'Media Error') {
      setCameraError(true);
      console.log('📹 Camera failed, switching to screen share...');
      handleScreenShare();
    }
  }, [connectionStatus]);

  // Auto-join room once
  useEffect(() => {
    if (!roomId || !socket || hasJoinedRef.current) return;
    
    console.log('🎬 VideoCallRoom mounted with roomId:', roomId);
    
    if (socket.connected) {
      console.log('📞 Auto-joining room:', roomId);
      hasJoinedRef.current = true;
      joinRoom();
    } else {
      const onConnect = () => {
        console.log('🔌 Socket connected, now joining room');
        hasJoinedRef.current = true;
        joinRoom();
      };
      
      socket.on('connect', onConnect);
      return () => socket.off('connect', onConnect);
    }
  }, [roomId, socket]);

  useEffect(() => {
    // Reassign stream when switching between book sharing and normal view
    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [sharedBook]);

  // Auto-call users once when ready
  useEffect(() => {
    if (!isInRoom || !localStreamRef.current || roomUsers.length <= 1 || hasCalledRef.current) {
      return;
    }
    
    console.log('✅ Ready to call - Room users:', roomUsers.length);
    hasCalledRef.current = true;
    
    const callTimer = setTimeout(() => {
      console.log('🚀 Calling all users in room');
      callAllUsers();
      setConnectionAttempts(prev => prev + 1);
    }, 2000);

    return () => clearTimeout(callTimer);
  }, [isInRoom, roomUsers.length, callAllUsers]);

  // Monitor connection health and show reconnect button if needed
  useEffect(() => {
    if (!isInRoom) return;

    reconnectTimerRef.current = setTimeout(() => {
      if (roomUsers.length > 1 && Object.keys(remoteStreams).length === 0) {
        console.log('⚠️ No remote streams detected after 10s, showing reconnect button');
        setShowReconnectButton(true);
      }
    }, 10000);

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, [isInRoom, roomUsers.length, remoteStreams]);

  // Hide reconnect button when streams appear
  useEffect(() => {
    if (Object.keys(remoteStreams).length > 0) {
      setShowReconnectButton(false);
    }
  }, [remoteStreams]);

  const handleReconnect = () => {
    console.log('🔄 Manual reconnect triggered');
    setShowReconnectButton(false);
    hasCalledRef.current = false;
    setConnectionAttempts(prev => prev + 1);
    
    roomUsers.forEach(userId => {
      if (userId !== socket?.id) {
        console.log(`🔄 Force reconnecting to ${userId}`);
        forceReconnectToPeer(userId);
      }
    });

    setTimeout(() => {
      if (!hasCalledRef.current) {
        hasCalledRef.current = true;
        callAllUsers();
      }
    }, 2000);
  };

  const handleVideoToggle = async () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      
      if (videoTrack && isVideoOn) {
        // Turning OFF video
        videoTrack.enabled = false;
        setIsVideoOn(false);
      } else {
        // Turning ON video - need to get new stream if track doesn't exist or is stopped
        if (!videoTrack || videoTrack.readyState === 'ended') {
          try {
            console.log('🎥 Getting new camera stream...');
            const newStream = await navigator.mediaDevices.getUserMedia({ 
              video: { width: 1280, height: 720 }, 
              audio: false // Keep existing audio
            });
            
            const newVideoTrack = newStream.getVideoTracks()[0];
            
            // Keep existing audio track
            const existingAudioTrack = localStreamRef.current.getAudioTracks()[0];
            
            // Create new stream with new video and existing audio
            const updatedStream = new MediaStream([
              newVideoTrack,
              ...(existingAudioTrack ? [existingAudioTrack] : [])
            ]);
            
            localStreamRef.current = updatedStream;
            
            // Update local video element
            if (localVideoRef.current) {
              localVideoRef.current.srcObject = updatedStream;
            }
            
            // Replace video track in all peer connections
            Object.values(peerConnectionsRef.current).forEach(pc => {
              const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
              if (sender) {
                sender.replaceTrack(newVideoTrack);
              }
            });
            
            setIsVideoOn(true);
            setIsScreenSharing(false); // No longer screen sharing
          } catch (error) {
            console.error('Failed to restart camera:', error);
            alert('Could not access camera. Please check permissions.');
          }
        } else {
          // Track exists and is not ended, just enable it
          videoTrack.enabled = true;
          setIsVideoOn(true);
        }
      }
    }
  };

  const handleAudioToggle = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioOn(audioTrack.enabled);
      }
    }
  };

  const handleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        // Start screen sharing
        const success = await hookStartScreenShare();
        setIsScreenSharing(success);
      } else {
        // Stop screen sharing and return to camera
        console.log('🔄 Stopping screen share, returning to camera...');
        
        // Get camera stream again
        const cameraStream = await navigator.mediaDevices.getUserMedia({ 
          video: { width: 1280, height: 720 }, 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        
        // Update local stream ref
        localStreamRef.current = cameraStream;
        
        // Update local video element
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = cameraStream;
        }
        
        // Replace tracks in all peer connections
        Object.values(peerConnectionsRef.current).forEach(pc => {
          const senders = pc.getSenders();
          cameraStream.getTracks().forEach(track => {
            const sender = senders.find(s => s.track && s.track.kind === track.kind);
            if (sender) {
              sender.replaceTrack(track);
            }
          });
        });
        
        setIsScreenSharing(false);
        setIsVideoOn(true); // Ensure video is marked as on
      }
    } catch (error) {
      console.error('Screen share toggle failed:', error);
      setIsScreenSharing(false);
    }
  };

  const handleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleEndCall = async () => {
    console.log('📵 Ending call');
    
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }

    Object.values(peerConnectionsRef.current).forEach(pc => pc.close());

    try {
      await fetch(`${API_URL}/api/video-call/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ room_id: roomId })
      });
    } catch (error) {
      console.error('Failed to end call on backend:', error);
    }

    onLeave();
  };

  const getGridLayout = () => {
    const totalStreams = 1 + Object.keys(remoteStreams).length;
    
    if (totalStreams === 1) return { columns: 1, rows: 1 };
    if (totalStreams === 2) return { columns: 2, rows: 1 };
    if (totalStreams <= 4) return { columns: 2, rows: 2 };
    if (totalStreams <= 6) return { columns: 3, rows: 2 };
    if (totalStreams <= 9) return { columns: 3, rows: 3 };
    return { columns: 4, rows: Math.ceil(totalStreams / 4) };
  };

  const gridLayout = getGridLayout();
  const totalParticipants = roomUsers.length;
  const connectedStreams = Object.keys(remoteStreams).length;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000
      }}
    >
      {/* Camera Error Banner */}
      {cameraError && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: 'white',
          padding: '12px 24px',
          textAlign: 'center',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
        }}>
          <Monitor size={20} />
          Camera unavailable - Screen sharing instead
        </div>
      )}

      {/* Connection Status Banner */}
      {isInRoom && totalParticipants > 1 && connectedStreams === 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
          color: 'white',
          padding: '12px 24px',
          textAlign: 'center',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          animation: 'pulse 2s infinite'
        }}>
          <Loader size={20} className="animate-spin" />
          Connecting to other participants...
        </div>
      )}

      {/* Header */}
      <div style={{
        padding: sharedBook ? '12px 24px' : '20px 32px',
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(20px)',
        borderBottom: '2px solid rgba(16, 185, 129, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.3s ease'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: sharedBook ? '12px' : '16px' }}>
          <div style={{
            width: sharedBook ? '36px' : '48px',
            height: sharedBook ? '36px' : '48px',
            borderRadius: sharedBook ? '8px' : '12px',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
            transition: 'all 0.3s ease'
          }}>
            <Users size={sharedBook ? 18 : 24} style={{ color: 'white' }} />
          </div>
          
          <div>
            {sharedBook ? (
              // Compact single-line layout when book is shared
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <h2 style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  Family Call
                </h2>
                <div style={{
                  width: '1px',
                  height: '16px',
                  background: 'rgba(255, 255, 255, 0.2)'
                }} />
                <span style={{
                  fontSize: '12px',
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontWeight: '500'
                }}>
                  <div style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: '#10b981',
                    animation: 'pulse 2s infinite'
                  }} />
                  {totalParticipants} in call
                </span>
                {connectedStreams > 0 && (
                  <>
                    <div style={{
                      width: '1px',
                      height: '16px',
                      background: 'rgba(255, 255, 255, 0.2)'
                    }} />
                    <span style={{
                      fontSize: '12px',
                      color: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontWeight: '500'
                    }}>
                      <CheckCircle size={12} />
                      Connected
                    </span>
                  </>
                )}
              </div>
            ) : (
              // Full layout when no book is shared
              <>
                <h2 style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: '700',
                  color: 'white',
                  letterSpacing: '-0.5px'
                }}>
                  Family Video Call
                </h2>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '4px'
                }}>
                  <span style={{
                    fontSize: '13px',
                    color: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '6px',
                    fontWeight: '600'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: '#10b981',
                      animation: 'pulse 2s infinite'
                    }} />
                    {totalParticipants} participant{totalParticipants !== 1 ? 's' : ''}
                  </span>
                  
                  <span style={{
                    fontSize: '13px',
                    color: connectedStreams > 0 ? '#10b981' : '#f59e0b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    background: connectedStreams > 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '6px',
                    fontWeight: '600'
                  }}>
                    {connectedStreams > 0 ? <CheckCircle size={14} /> : <Loader size={14} className="animate-spin" />}
                    {connectedStreams} connected
                  </span>

                  <span style={{
                    fontSize: '13px',
                    color: '#888',
                    fontWeight: '500'
                  }}>
                    {connectionStatus}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right side buttons remain the same but with adjusted sizing */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {showReconnectButton && (
            <button
              onClick={handleReconnect}
              style={{
                padding: sharedBook ? '8px 12px' : '10px 16px',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                border: 'none',
                borderRadius: sharedBook ? '8px' : '10px',
                cursor: 'pointer',
                color: 'white',
                fontSize: sharedBook ? '12px' : '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)',
                animation: 'pulse 2s infinite'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
              }}
            >
              <RefreshCw size={sharedBook ? 14 : 16} />
              Reconnect
            </button>
          )}
          
          <button
            onClick={handleFullscreen}
            style={{
              padding: sharedBook ? '8px' : '10px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: sharedBook ? '8px' : '10px',
              cursor: 'pointer',
              color: 'white',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            <Maximize2 size={sharedBook ? 16 : 20} />
          </button>
          
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            style={{
              padding: sharedBook ? '8px' : '10px',
              background: showDebugPanel ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
              border: showDebugPanel ? '1px solid #10b981' : '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: sharedBook ? '8px' : '10px',
              cursor: 'pointer',
              color: showDebugPanel ? '#10b981' : 'white',
              transition: 'all 0.2s ease'
            }}
          >
            <Settings size={sharedBook ? 16 : 20} />
          </button>
        </div>
      </div>

      {/* Video Grid - Dynamic Layout based on book sharing */}
      <div style={{
        flex: 1,
        display: sharedBook ? 'flex' : 'grid',
        flexDirection: sharedBook ? 'column' : undefined,
        gridTemplateColumns: !sharedBook ? `repeat(${gridLayout.columns}, 1fr)` : undefined,
        gridTemplateRows: !sharedBook ? `repeat(${gridLayout.rows}, 1fr)` : undefined,
        gap: '16px',
        padding: '24px',
        overflow: 'auto',
        background: 'rgba(0, 0, 0, 0.3)'
      }}>
        {sharedBook ? (
          <>
            {/* Small video feeds at the top when book is shared */}
            <div style={{
              display: 'flex',
              gap: '12px',
              height: '120px',
              flexShrink: 0,
              overflowX: 'auto',
              padding: '8px',
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '12px'
            }}>
              {/* Local Video Thumbnail - Always show this */}
              <div style={{
                position: 'relative',
                minWidth: '160px',
                height: '100%',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '2px solid #10b981',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
              }}>
                <video
                  autoPlay
                  muted
                  playsInline
                  ref={(el) => {
                    // Use a callback ref to handle both the main ref and this thumbnail
                    if (el && localStreamRef.current) {
                      el.srcObject = localStreamRef.current;
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transform: (isScreenSharing || isUsingScreenShare) ? 'none' : 'scaleX(-1)'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: '4px',
                  left: '8px',
                  padding: '2px 8px',
                  background: 'rgba(16, 185, 129, 0.9)',
                  borderRadius: '4px',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  You {isBookSharer && '(Sharing)'}
                </div>
                
                {/* Audio/Video indicators */}
                <div style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  display: 'flex',
                  gap: '4px'
                }}>
                  <div style={{
                    padding: '4px',
                    background: isVideoOn ? 'rgba(16, 185, 129, 0.9)' : 'rgba(239, 68, 68, 0.9)',
                    borderRadius: '4px',
                  }}>
                    {isVideoOn ? <Video size={10} style={{ color: 'white' }} /> : <VideoOff size={10} style={{ color: 'white' }} />}
                  </div>
                  <div style={{
                    padding: '4px',
                    background: isAudioOn ? 'rgba(16, 185, 129, 0.9)' : 'rgba(239, 68, 68, 0.9)',
                    borderRadius: '4px',
                  }}>
                    {isAudioOn ? <Mic size={10} style={{ color: 'white' }} /> : <MicOff size={10} style={{ color: 'white' }} />}
                  </div>
                </div>
              </div>

              {/* Remote Video Thumbnails */}
              {Object.entries(remoteStreams).map(([userId, stream]) => {
                const participant = participants?.find(p => p.id.toString() === userId);
                const userName = participant 
                  ? `${participant.first_name} ${participant.last_name}`
                  : `User ${userId.slice(-4)}`;

                return (
                  <div key={userId} style={{
                    position: 'relative',
                    minWidth: '160px',
                    height: '100%',
                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '2px solid #8b5cf6',
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)'
                  }}>
                    <video
                      autoPlay
                      playsInline
                      ref={el => {
                        if (el && stream) el.srcObject = stream;
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '4px',
                      left: '8px',
                      padding: '2px 8px',
                      background: 'rgba(139, 92, 246, 0.9)',
                      borderRadius: '4px',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '600',
                      maxWidth: '140px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {userName}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Main Book Display */}
            <div style={{
              flex: 1,
              position: 'relative',
              background: 'white',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
            }}>
              {/* Book Header Bar */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <BookOpen size={20} style={{ color: 'white' }} />
                  <div>
                    <h3 style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: '700',
                      color: 'white'
                    }}>
                      {sharedBook.title}
                    </h3>
                    <p style={{
                      margin: 0,
                      fontSize: '12px',
                      color: 'rgba(255, 255, 255, 0.9)'
                    }}>
                      by {sharedBook.author}
                    </p>
                  </div>
                </div>
                
                {isBookSharer && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      You're sharing
                    </span>
                    <button
                      onClick={() => {
                        setSharedBook(null);
                        setIsBookSharer(false);
                        if (socket) {
                          socket.emit('unshare-book', { roomId });
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(239, 68, 68, 0.9)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(220, 38, 38, 0.9)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.9)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <X size={16} />
                      Stop Sharing
                    </button>
                  </div>
                )}

                {!isBookSharer && (
                  <div style={{
                    padding: '4px 12px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    Shared by {participants?.find(p => p.id === sharedBook.sharerId)?.first_name || 'Someone'}
                  </div>
                )}
              </div>

              {/* Book PDF Viewer */}
              <iframe
                ref={bookIframeRef}
                src={`${sharedBook.pdf_url}#page=${sharedBook.currentPage || 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  paddingTop: '60px' // Space for header
                }}
                title={sharedBook.title}
                onLoad={handleBookIframeLoad}
                // Allow scrolling for the sharer, prevent for viewers
                // {...(!isBookSharer && { 
                //   pointerEvents: 'none',
                //   scrolling: 'no' 
                // })}
              />

              {/* Overlay for non-sharers to prevent interaction */}
              {!isBookSharer && (
                <div style={{
                  position: 'absolute',
                  top: '60px',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'transparent',
                  cursor: 'not-allowed',
                  pointerEvents: 'none'
                }} 
                title="Only the person sharing can control the book" />
              )}
            </div>
          </>
        ) : (
          <>
            {/* Normal grid layout when no book is shared */}
            {/* Local Video */}
            <div style={{
              position: 'relative',
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '3px solid #10b981',
              boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
              transition: 'all 0.3s ease'
            }}>
              <video
                autoPlay
                muted
                playsInline
                ref={(el) => {
                  // Update both the ref and set the stream
                  if (el) {
                    localVideoRef.current = el;
                    if (localStreamRef.current) {
                      el.srcObject = localStreamRef.current;
                    }
                  }
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transform: (isScreenSharing || isUsingScreenShare) ? 'none' : 'scaleX(-1)',
                  background: '#000'
                }}
              />

              {/* Local Video Overlay */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 50%)',
                pointerEvents: 'none'
              }} />

              <div style={{
                position: 'absolute',
                bottom: '16px',
                left: '16px',
                right: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pointerEvents: 'none'
              }}>
                <div style={{
                  padding: '8px 16px',
                  background: 'rgba(16, 185, 129, 0.95)',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '700',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                }}>
                  You {(isScreenSharing || isUsingScreenShare) && '(Screen)'}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <div style={{
                    padding: '8px',
                    background: isVideoOn ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
                    borderRadius: '10px',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }}>
                    {isVideoOn ? <Video size={16} style={{ color: 'white' }} /> : <VideoOff size={16} style={{ color: 'white' }} />}
                  </div>
                  <div style={{
                    padding: '8px',
                    background: isAudioOn ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
                    borderRadius: '10px',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }}>
                    {isAudioOn ? <Mic size={16} style={{ color: 'white' }} /> : <MicOff size={16} style={{ color: 'white' }} />}
                  </div>
                </div>
              </div>
            </div>

            {/* Remote Videos */}
            {Object.entries(remoteStreams).map(([userId, stream]) => {
              const participant = participants?.find(p => p.id.toString() === userId);
              const userName = participant 
                ? `${participant.first_name} ${participant.last_name}`
                : `User ${userId.slice(-4)}`;

              return (
                <div key={userId} style={{
                  position: 'relative',
                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '3px solid #8b5cf6',
                  boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
                  transition: 'all 0.3s ease'
                }}>
                  <RemoteVideo
                    userId={userId}
                    username={userName}
                    stream={stream}
                    onReconnectRequest={forceReconnectToPeer}
                    isMobile={false}
                  />
                </div>
              );
            })}

            {/* Empty State if no remote streams */}
            {isInRoom && totalParticipants > 1 && connectedStreams === 0 && (
              <div style={{
                gridColumn: '1 / -1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
                gap: '16px'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(59, 130, 246, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px'
                }}>
                  <Loader size={40} style={{ color: '#3b82f6' }} className="animate-spin" />
                </div>
                <h3 style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  Connecting to participants...
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#888',
                  textAlign: 'center',
                  maxWidth: '400px'
                }}>
                  Waiting for video streams to connect. This usually takes a few seconds.
                </p>
                <div style={{
                  marginTop: '8px',
                  padding: '8px 16px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  fontSize: '13px',
                  color: '#3b82f6'
                }}>
                  Attempt {connectionAttempts} • {totalParticipants - 1} user{totalParticipants - 1 !== 1 ? 's' : ''} in room
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Controls */}
      <div style={{
          padding: sharedBook ? '12px 24px' : '24px 32px',
          background: 'rgba(0, 0, 0, 0.95)',
          backdropFilter: 'blur(20px)',
          borderTop: '2px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: sharedBook ? '8px' : '16px',
          boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s ease',
          position: 'relative'
        }}>
          {sharedBook ? (
            <>
              {/* Minimized Controls when book is shared */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '8px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {/* Compact control buttons */}
                <button
                  onClick={handleVideoToggle}
                  style={{
                    padding: '10px',
                    background: isVideoOn ? 'transparent' : 'rgba(239, 68, 68, 0.8)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: isVideoOn ? '#10b981' : 'white',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={isVideoOn ? 'Turn off camera' : 'Turn on camera'}
                >
                  {isVideoOn ? <Video size={18} /> : <VideoOff size={18} />}
                </button>

                <button
                  onClick={handleAudioToggle}
                  style={{
                    padding: '10px',
                    background: isAudioOn ? 'transparent' : 'rgba(239, 68, 68, 0.8)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: isAudioOn ? '#10b981' : 'white',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={isAudioOn ? 'Mute' : 'Unmute'}
                >
                  {isAudioOn ? <Mic size={18} /> : <MicOff size={18} />}
                </button>

                <div style={{
                  width: '1px',
                  height: '24px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  margin: '0 4px'
                }} />

                {/* Book control - highlighted since it's active */}
                <button
                  onClick={() => setShowBookModal(true)}
                  style={{
                    padding: '10px',
                    background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: 'white',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(251, 191, 36, 0.4)'
                  }}
                  title="Change book"
                >
                  <BookOpen size={18} />
                </button>

                <button
                  onClick={handleScreenShare}
                  style={{
                    padding: '10px',
                    background: isScreenSharing ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: isScreenSharing ? 'white' : '#6b7280',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={isScreenSharing ? 'Stop screen share' : 'Share screen'}
                >
                  {isScreenSharing ? <MonitorOff size={18} /> : <Monitor size={18} />}
                </button>

                <div style={{
                  width: '1px',
                  height: '24px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  margin: '0 4px'
                }} />

                <button
                  onClick={handleEndCall}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <PhoneOff size={16} />
                  End
                </button>
              </div>

              {/* Expand button hint */}
              <div style={{
                position: 'absolute',
                top: '-20px',
                left: '50%',
                transform: 'translateX(-50%)',
                padding: '2px 12px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px 8px 0 0',
                fontSize: '11px',
                color: 'rgba(255, 255, 255, 0.5)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onClick={() => {
                // Option to stop book sharing and expand controls
                setSharedBook(null);
                setIsBookSharer(false);
                if (socket) {
                  socket.emit('unshare-book', { roomId });
                }
              }}
              >
                Click to expand controls
              </div>
            </>
          ) : (
            <>
              {/* Full-size controls when no book is shared */}
              <button
                onClick={handleVideoToggle}
                style={{
                  padding: '18px',
                  background: isVideoOn ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  border: isVideoOn ? '2px solid rgba(255, 255, 255, 0.2)' : 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  boxShadow: isVideoOn ? 'none' : '0 4px 12px rgba(239, 68, 68, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.background = isVideoOn ? 'rgba(255, 255, 255, 0.15)' : 'linear-gradient(135deg, #dc2626, #b91c1c)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = isVideoOn ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #ef4444, #dc2626)';
                }}
              >
                {isVideoOn ? <Video size={24} /> : <VideoOff size={24} />}
              </button>

              <button
                onClick={handleAudioToggle}
                style={{
                  padding: '18px',
                  background: isAudioOn ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  border: isAudioOn ? '2px solid rgba(255, 255, 255, 0.2)' : 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  boxShadow: isAudioOn ? 'none' : '0 4px 12px rgba(239, 68, 68, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.background = isAudioOn ? 'rgba(255, 255, 255, 0.15)' : 'linear-gradient(135deg, #dc2626, #b91c1c)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = isAudioOn ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #ef4444, #dc2626)';
                }}
              >
                {isAudioOn ? <Mic size={24} /> : <MicOff size={24} />}
              </button>

              <button
                onClick={() => setShowBookModal(true)}
                style={{
                  padding: '18px',
                  background: sharedBook ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : 'rgba(255, 255, 255, 0.1)',
                  border: sharedBook ? 'none' : '2px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  boxShadow: sharedBook ? '0 4px 12px rgba(251, 191, 36, 0.4)' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.background = sharedBook ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = sharedBook ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <BookOpen size={24} />
              </button>

              <button
                onClick={handleScreenShare}
                style={{
                  padding: '18px',
                  background: isScreenSharing ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255, 255, 255, 0.1)',
                  border: isScreenSharing ? 'none' : '2px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  color: 'white',
                  transition: 'all 0.2s ease',
                  boxShadow: isScreenSharing ? '0 4px 12px rgba(16, 185, 129, 0.4)' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.background = isScreenSharing ? 'linear-gradient(135deg, #059669, #047857)' : 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = isScreenSharing ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255, 255, 255, 0.1)';
                }}
              >
                {isScreenSharing ? <MonitorOff size={24} /> : <Monitor size={24} />}
              </button>

              <div style={{
                width: '2px',
                height: '40px',
                background: 'rgba(255, 255, 255, 0.1)',
                margin: '0 8px'
              }} />

              <button
                onClick={handleEndCall}
                style={{
                  padding: '18px 36px',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  border: 'none',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #dc2626, #b91c1c)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444, #dc2626)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                }}
              >
                <PhoneOff size={20} />
                End Call
              </button>
            </>
          )}
        </div>

      {/* Debug Panel */}
      {showDebugPanel && (
        <VideoCallDebugPanel
          socket={socket}
          localStream={localStreamRef.current}
          remoteStreams={remoteStreams}
          peerConnections={peerConnectionsRef}
          isVisible={showDebugPanel}
        />
      )}

      <BookSharingModal
        isOpen={showBookModal}
        onClose={() => setShowBookModal(false)}
        onSelectBook={handleBookSelect}
      />

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.02);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};